import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { packOrder, closePack } from "@/lib/fulfillment.functions";
import { getPackOrder, listPackOrderAllocations, listPackOrderSkus, getPackingSlip } from "@/lib/pack-detail.functions";
import { purchaseShippingLabel } from "@/lib/shipping-labels.functions";
import { listServicesForPack } from "@/lib/carrier-catalog.functions";
import { setShipmentBolFlags, evaluateBolRequirement } from "@/lib/bol.functions";
import { runServerFn } from "@/lib/server-fn";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/components/ui/page-header";
import { StatusPill, statusTone } from "@/components/ui/status-pill";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, Package, Plus, Printer, ScanLine, Trash2, Check, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { RateShopPanel } from "@/components/pack/rate-shop-panel";
import { RoutingExplanationPanel } from "@/components/pack/routing-explanation-panel";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { labelaryPreviewUrl, packingSlipZpl } from "@/lib/printing/zpl";
import { probeBrowserPrint, resolvePrinter, sendZpl } from "@/lib/printing/browser-print";

export const Route = createFileRoute("/_authenticated/app/pack/$orderId")({ component: PackStation });

type CartonItem = { allocation_id: string; sku_id: string; sku_label: string; qty: number; track_serials: boolean; serials: string[] };
/**
 * Pass 3.5: pack-page Carton represents IN-PROGRESS build state only.
 * Once Pack Confirm fires, the server (`pack_order` RPC + policy-driven
 * `seal_shipment_cartons_with_sscc`) becomes the authoritative source of
 * truth for cartons, carton_seq, closed_at, and SSCCs. The browser must
 * never generate an SSCC.
 */
type Carton = { id: string; weight_oz: string; weight_manual: boolean; size: string; items: CartonItem[] };

const OZ_TO_G = 28.3495;

/**
 * Carriers surfaced in the parcel dropdown. Service catalogs (including
 * provider-native service codes) are fetched from the server per-carrier —
 * see `listServicesForPack`. Codes match what Rate Shop returns exactly,
 * so the dropdown and Rate Shop stay in sync.
 */
const CARRIERS = ["UPS", "USPS", "GlobalPost", "FedEx", "DHL"];

function labelProviderFor(carrier: string): "ups" | "usps" | null {
  if (carrier === "UPS") return "ups";
  if (carrier === "USPS") return "usps";
  return null;
}

function normalizeCarrier(c: string | null | undefined): string {
  if (!c) return "";
  const m = CARRIERS.find((x) => x.toLowerCase() === c.toLowerCase());
  return m ?? c;
}

/** Common carton size presets — code is sent to the backend as package_type. */
const CARTON_SIZES = [
  { code: "small",  label: "Small  · 9 × 6 × 4 in" },
  { code: "medium", label: "Medium · 12 × 9 × 6 in" },
  { code: "large",  label: "Large  · 16 × 12 × 8 in" },
  { code: "xlarge", label: "X-Large · 20 × 16 × 12 in" },
  { code: "mailer", label: "Poly mailer" },
  { code: "custom", label: "Custom / other" },
] as const;

function PackStation() {
  const { orderId } = Route.useParams();
  const navigate = useNavigate();
  const packOrderFn = useServerFn(packOrder);
  const closePackFn = useServerFn(closePack);
  const buyLabelFn = useServerFn(purchaseShippingLabel);
  const getOrderFn = useServerFn(getPackOrder);
  const listAllocsFn = useServerFn(listPackOrderAllocations);
  const listSkusFn = useServerFn(listPackOrderSkus);
  const getSlipFn = useServerFn(getPackingSlip);
  const listServicesFn = useServerFn(listServicesForPack);
  const setBolFlagsFn = useServerFn(setShipmentBolFlags);
  const evalBolFn = useServerFn(evaluateBolRequirement);

  const { data: order } = useQuery({
    queryKey: ["pack-order", orderId],
    queryFn: () => getOrderFn({ data: { orderId } }),
    refetchInterval: 10_000,
    refetchIntervalInBackground: false,
  });
  const { data: allocations = [] } = useQuery({
    queryKey: ["pack-allocs", orderId],
    queryFn: () => listAllocsFn({ data: { orderId } }),
  });
  const { data: skus = [] } = useQuery({
    queryKey: ["pack-skus", order?.customer_id], enabled: !!order?.customer_id,
    queryFn: () => listSkusFn({ data: { customerId: order!.customer_id } }),
  });
  const skuLabel = (id: string) => { const s = skus.find((x) => x.id === id); return s ? `${s.sku_code} — ${s.name}` : id.slice(0, 6); };
  const skuWeightOz = (id: string) => {
    const s = skus.find((x) => x.id === id);
    const g = s?.weight_grams ? Number(s.weight_grams) : 0;
    return g > 0 ? g / OZ_TO_G : 0;
  };
  const autoCartonOz = (c: Carton) => c.items.reduce((s, i) => s + skuWeightOz(i.sku_id) * i.qty, 0);
  const effectiveCartonOz = (c: Carton) => (c.weight_manual ? (Number(c.weight_oz) || 0) : autoCartonOz(c));
  const displayCartonOz = (c: Carton) => {
    if (c.weight_manual) return c.weight_oz;
    const v = autoCartonOz(c);
    return v > 0 ? v.toFixed(2) : "";
  };

  const [cartons, setCartons] = useState<Carton[]>([
    { id: crypto.randomUUID(), weight_oz: "", weight_manual: false, size: "medium", items: [] },
  ]);
  const [active, setActive] = useState(0);
  const [scan, setScan] = useState("");
  const [tracking, setTracking] = useState("");
  const [chosenRate, setChosenRate] = useState<{
    carrier: string; service: string; amount: number;
    /** Sprint 25 — execution identity of the chosen rate. */
    connection_id?: string;
    /** Sprint 25 — provider-native service code preserved end-to-end. */
    service_code?: string;
    estimated_delivery_date?: string | null;
    estimated_delivery_source?: "carrier_api" | "service_table" | "zip_pair_fallback" | "unknown";
  } | null>(null);
  const [carrier, setCarrier] = useState<string>("");
  const [service, setService] = useState<string>("");
  const [freightMode, setFreightMode] = useState<"parcel" | "ltl" | "ftl" | "partial_tl">("parcel");
  const [busy, setBusy] = useState(false);
  const scanRef = useRef<HTMLInputElement>(null);

  // Per-carrier service catalog (provider-native codes + display names +
  // per-customer active flags). Drives the Service dropdown and the
  // display-name lookup for the "Selected rate" line.
  const { data: catalogServices = [] } = useQuery({
    queryKey: ["pack-carrier-catalog", (order as any)?.tenant_id, order?.customer_id, carrier],
    enabled: !!(order as any)?.tenant_id && !!carrier,
    queryFn: () =>
      listServicesFn({
        data: {
          tenantId: (order as any).tenant_id,
          customerId: order?.customer_id ?? null,
          carrierCode: carrier,
        },
      }),
  });
  const activeServices = catalogServices.filter((s) => s.active);
  const serviceDisplayFor = (code: string): string =>
    catalogServices.find((s) => s.service_code === code)?.display_name ?? code;

  // Remaining qty per allocation = qty - already packed across cartons
  const remainingByAlloc = useMemo(() => {
    const m = new Map<string, number>();
    for (const a of allocations) m.set(a.id, a.qty);
    for (const c of cartons) for (const i of c.items) m.set(i.allocation_id, (m.get(i.allocation_id) ?? 0) - i.qty);
    return m;
  }, [allocations, cartons]);
  const totalRemaining = Array.from(remainingByAlloc.values()).reduce((s, n) => s + Math.max(0, n), 0);

  // Per-SKU rollup across all allocations: ordered vs packed vs remaining
  const skuRollup = useMemo(() => {
    const m = new Map<string, { sku_id: string; ordered: number; packed: number }>();
    for (const a of allocations) {
      const cur = m.get(a.sku_id) ?? { sku_id: a.sku_id, ordered: 0, packed: 0 };
      cur.ordered += a.qty;
      m.set(a.sku_id, cur);
    }
    for (const c of cartons) for (const i of c.items) {
      const cur = m.get(i.sku_id) ?? { sku_id: i.sku_id, ordered: 0, packed: 0 };
      cur.packed += i.qty;
      m.set(i.sku_id, cur);
    }
    return Array.from(m.values()).sort((a, b) => skuLabel(a.sku_id).localeCompare(skuLabel(b.sku_id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allocations, cartons, skus]);

  useEffect(() => { scanRef.current?.focus(); }, []);

  // Seed carrier/service from the order once it loads.
  useEffect(() => {
    if (!order) return;
    const cust = (order as any).customers as { gs1_prefix?: string | null; default_carrier?: string | null; default_service?: string | null } | null;
    const c = normalizeCarrier(order.carrier) || normalizeCarrier(cust?.default_carrier ?? null);
    if (c) setCarrier((cur) => cur || c);
    const svc = order.service_level || cust?.default_service;
    if (svc) setService((cur) => cur || svc);
  }, [order]);

  const handleScan = (e: React.FormEvent) => {
    e.preventDefault();
    if (order?.status === "cancelled") {
      setScan("");
      return toast.error("Order was cancelled — return picked units to source instead of packing");
    }
    const code = scan.trim();
    if (!code) return;
    // Find SKU by sku_code or barcode
    const sku = skus.find((s) => s.sku_code === code || s.barcode === code || s.sku_code.toLowerCase() === code.toLowerCase());
    if (!sku) { setScan(""); return toast.error("Unknown SKU/barcode"); }
    // Find allocation with remaining qty > 0
    const alloc = allocations.find((a) => a.sku_id === sku.id && (remainingByAlloc.get(a.id) ?? 0) > 0);
    if (!alloc) { setScan(""); return toast.error("Nothing left to pack for this SKU"); }
    setCartons((prev) => prev.map((c, idx) => {
      if (idx !== active) return c;
      const existing = c.items.find((i) => i.allocation_id === alloc.id);
      if (existing) {
        return { ...c, items: c.items.map((i) => i.allocation_id === alloc.id ? { ...i, qty: i.qty + 1 } : i) };
      }
      return { ...c, items: [...c.items, { allocation_id: alloc.id, sku_id: sku.id, sku_label: skuLabel(sku.id), qty: 1, track_serials: !!sku.track_serials, serials: [] }] };
    }));
    setScan(""); scanRef.current?.focus();
  };

  const addCarton = () => {
    setCartons((p) => [...p, { id: crypto.randomUUID(), weight_oz: "", weight_manual: false, size: "medium", items: [] }]);
    setActive(cartons.length);
  };
  const removeItem = (cartonIdx: number, allocId: string) => {
    setCartons((p) => p.map((c, i) => i === cartonIdx ? { ...c, items: c.items.filter((it) => it.allocation_id !== allocId) } : c));
  };
  const removeCarton = (idx: number) => {
    if (cartons.length <= 1) return;
    setCartons((p) => p.filter((_, i) => i !== idx));
    setActive(0);
  };

  /** Open a printable preview (Labelary) for a carton's shipping label. */
  const printCartonLabel = (idx: number) => {
    const c = cartons[idx];
    if (!order) return;
    const ref = `${order.order_number} · Carton ${idx + 1} of ${cartons.length}`;
    // Lightweight placeholder shipping label (real carrier ZPL comes from buyShippingLabel).
    const zpl = [
      "^XA", "^PW812", "^LL1218", "^CI28",
      `^FO20,40^A0N,48,48^FD${(carrier || "CARRIER").toUpperCase()}^FS`,
      `^FO20,110^A0N,28,28^FD${service ?? ""}^FS`,
      `^FO20,180^A0N,32,32^FDOrder ${order.order_number}^FS`,
      `^FO20,230^A0N,28,28^FDCarton ${idx + 1} of ${cartons.length}^FS`,
      `^FO20,280^A0N,24,24^FDSize: ${c.size}${c.weight_oz ? `   ·   ${c.weight_oz} oz` : ""}^FS`,
      tracking ? `^FO40,360^BY3^BCN,200,Y,N,N^FD${tracking}^FS` : "",
      "^XZ",
    ].filter(Boolean).join("\n");
    window.open(labelaryPreviewUrl(zpl), "_blank", "noopener,noreferrer");
  };

  const finalize = async (mode: "label" | "ship_only") => {
    if (!order) return;
    if (cartons.every((c) => c.items.length === 0)) return toast.error("Add at least one item to a carton");
    const isFreight = freightMode !== "parcel";
    if (!carrier) return toast.error(isFreight ? "Enter a freight carrier" : "Select a carrier");
    if (!isFreight && !service) return toast.error("Select a service type");
    const provider = isFreight ? null : labelProviderFor(carrier);
    // `service` now already holds the provider-native service_code
    // (e.g. UPS "03", USPS "usps_ground_advantage") — the dropdown stores
    // the code directly, so no display→code translation is needed here.
    const serviceCode = isFreight ? null : (service || null);
    if (isFreight) {
      // Freight always uses "ship_only" path — labels come from carrier; we generate a BOL instead.
      mode = "ship_only";
    } else if (mode === "label") {
      if (!provider) return toast.error(`${carrier} label purchase is not supported yet — use "Ship" if customer provides label`);
      if (!serviceCode) return toast.error(`No carrier code mapped for "${service}"`);
    } else {
      if (!confirm("Ship without purchasing a label? Use this only when the customer is providing the shipping label.")) return;
    }
    if (totalRemaining > 0 && !confirm(`${totalRemaining} unit(s) still unpacked. Ship anyway?`)) return;
    // Packing slip (per-customer toggle): try silent Zebra print on 4x6 first.
    // Fall back to the printable HTML route only when no Zebra agent/printer
    // is reachable.
    const cust = (order as any).customers as { packing_slip_enabled?: boolean | null } | null;
    const packingSlipOn = cust?.packing_slip_enabled !== false;
    if (packingSlipOn) {
      try {
        const slip = await getSlipFn({ data: { orderId: order.id } });
        const addr = (slip.order.ship_to ?? {}) as Record<string, string | undefined>;
        const skuById = new Map(slip.skus.map((s) => [s.id, s] as const));
        const zpl = packingSlipZpl({
          orderNumber: slip.order.order_number,
          customerPo: slip.order.customer_po,
          customerName: slip.customer?.name ?? null,
          carrier: slip.order.carrier,
          serviceLevel: slip.order.service_level,
          date: slip.order.created_at,
          shipTo: {
            name: addr.name,
            line1: addr.street1 ?? addr.address1,
            line2: addr.street2 ?? addr.address2,
            city: addr.city,
            region: addr.state ?? addr.region,
            postal: addr.zip ?? addr.postal_code ?? addr.postal,
            country: addr.country,
          },
          lines: slip.lines.map((l) => {
            const s = l.sku_id ? skuById.get(l.sku_id) : null;
            return { sku: s?.sku_code ?? "—", description: s?.name ?? l.description ?? "", qty: l.qty_ordered };
          }),
        });
        const status = await probeBrowserPrint();
        const printer = status.ok ? resolvePrinter(status, { name: "", address: "" }) : null;
        if (printer) {
          const res = await sendZpl(printer, zpl);
          if (!res.ok) throw new Error(res.error);
          toast.success(`Packing slip → ${printer.name}`);
        } else {
          toast.message("No Zebra printer detected — opening PDF fallback");
          const w = window.open(`/app/pack/${order.id}/packing-slip`, "_blank", "noopener,noreferrer");
          if (!w) toast.warning("Packing slip blocked by pop-up blocker");
        }
      } catch (e) {
        toast.error("Packing slip print failed — opening PDF fallback", {
          description: e instanceof Error ? e.message : String(e),
        });
        window.open(`/app/pack/${order.id}/packing-slip`, "_blank", "noopener,noreferrer");
      }
    }
    setBusy(true);
    const payload = cartons
      .filter((c) => c.items.length > 0)
      .map((c) => ({
        weight_grams: (() => {
          const oz = effectiveCartonOz(c);
          return oz > 0 ? String(Math.round(oz * OZ_TO_G)) : null;
        })(),
        package_type: c.size || "box",
        items: c.items.map((i) => ({
          allocation_id: i.allocation_id,
          qty: i.qty,
          ...(i.track_serials ? { serials: i.serials.map((s) => s.trim()).filter(Boolean) } : {}),
        })),
      }));
    // Client-side serial validation
    for (const c of payload) {
      for (const it of c.items) {
        if ("serials" in it) {
          const arr = (it as { serials: string[] }).serials;
          if (arr.length !== it.qty) {
            setBusy(false);
            return toast.error(`Serial-tracked item needs ${it.qty} serial(s); got ${arr.length}`);
          }
        }
      }
    }
    try {
      const { shipmentId } = await runServerFn(
        () => packOrderFn({
          data: {
            orderId: order.id,
            carrier,
            serviceLevel: service,
            tracking: tracking.trim() || "",
            cartons: payload,
          },
        }),
        mode === "label" ? "Pack & ship" : "Pack & mark shipped",
      );
      const filledIdxs = cartons
        .map((c, i) => ({ c, i }))
        .filter(({ c }) => c.items.length > 0)
        .map(({ i }) => i);
      // Pass 3.5: SSCC labels live on the server. After pack confirm, they
      // can be printed from the Shipment page via getShipmentCartonLabels.
      void filledIdxs;

      // Persist freight mode on the shipment so BOL evaluation + downstream
      // workflows see the right value.
      if (freightMode !== "parcel") {
        try {
          await setBolFlagsFn({ data: { shipmentId, freight_mode: freightMode } });
        } catch (e) {
          toast.warning("Could not set freight mode on shipment", {
            description: e instanceof Error ? e.message : String(e),
          });
        }
      }

      // Evaluate BOL requirement (freight mode OR customer routing guide).
      let bolRequired = false;
      try {
        const evalRes = await evalBolFn({ data: { shipmentId } });
        bolRequired = !!evalRes?.required;
      } catch { /* non-fatal */ }

      if (mode === "label") {
        try {
          const res = await runServerFn(
            () => buyLabelFn({
              data: {
                shipment_id: shipmentId,
                carrier: provider!,
                // Sprint 25 — prefer the chosen rate's provider-native code when present.
                service_code: chosenRate?.service_code ?? serviceCode!,
                // Sprint 25 — primary execution identity from the chosen rate.
                connection_id: chosenRate?.connection_id ?? null,
                idempotencyKey: `pack-${shipmentId}`,
                accept_address_warnings: true,
                estimated_delivery_date: chosenRate?.estimated_delivery_date ?? null,
                estimated_delivery_source: chosenRate?.estimated_delivery_source ?? null,
              },
            }),
            "Purchase shipping label",
          );
          toast.success(`Label purchased · ${res.tracking}`);
          if (res.label_url) window.open(res.label_url, "_blank", "noopener,noreferrer");
        } catch (e) {
          toast.error(
            "Shipment created but label purchase failed — buy label from the Shipment page",
            { description: e instanceof Error ? e.message : String(e) },
          );
        }
      } else {
        toast.success(
          freightMode !== "parcel"
            ? `Freight shipment created (${freightMode.toUpperCase()}) — generate BOL next`
            : `Packed ${payload.length} carton(s) — marked shipped (no label)`,
        );
      }
      if (bolRequired) {
        toast.message("Bill of Lading required — opening BOL editor");
        navigate({ to: "/app/shipments/$shipmentId/bol", params: { shipmentId } });
      } else {
        navigate({ to: "/app/shipments/$shipmentId", params: { shipmentId } });
      }
    } catch {
      // toast handled by runServerFn
    } finally {
      setBusy(false);
    }
  };

  if (!order) return <div className="p-4 sm:p-6 text-muted-foreground">Loading…</div>;

  const onClosePack = async () => {
    if (!confirm("Close packing on this order? Order moves to packed and can be shipped.")) return;
    setBusy(true);
    try {
      await runServerFn(() => closePackFn({ data: { orderId: order.id } }), "Close pack");
      toast.success("Pack closed — order ready to ship");
    } catch {
      // toast handled
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader
        title={
          <span className="flex items-center gap-3">
            <Link to="/app/pack" className="text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <span className="font-mono">Pack · {order.order_number}</span>
            <StatusPill tone={statusTone(order.status)} dot>{order.status}</StatusPill>
          </span>
        }
        description={`${carrier || "No carrier"}${service ? ` · ${service}` : ""} · ${totalRemaining} unit(s) remaining`}
        actions={
          (order.status === "picked" || order.status === "packed") ? (
            <Button size="sm" variant="outline" disabled={busy} onClick={onClosePack}>
              <Check className="h-4 w-4 mr-1" /> Close pack
            </Button>
          ) : null
        }
      />
      <div className="p-4 sm:p-6 space-y-4">
      {order.status === "cancelled" && (
        <div className="rounded-md border-2 border-destructive/40 bg-destructive/10 p-4 text-sm space-y-1">
          <div className="font-semibold text-destructive">Order cancelled</div>
          <p className="text-muted-foreground">
            This order was cancelled. Do not pack. Return any picked units to their
            source locations — putaway tasks should already be queued.
          </p>
          <Button
            size="sm"
            variant="outline"
            className="mt-2"
            onClick={() => navigate({ to: "/app/orders/$orderId", params: { orderId } })}
          >
            Open order detail
          </Button>
        </div>
      )}
      <Card>
        <CardContent className="space-y-3 pt-6">
          <form onSubmit={handleScan} className="flex gap-2">
            <div className="relative flex-1">
              <ScanLine className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input ref={scanRef} value={scan} onChange={(e) => setScan(e.target.value)} placeholder="Scan SKU or barcode…" className="pl-9 font-mono" autoFocus />
            </div>
            <Button type="submit" variant="outline">Add</Button>
          </form>
        </CardContent>
      </Card>

      {/* Required items panel — what was picked / what still needs to be packed */}
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-sm">Items to Pack</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {skuRollup.length === 0 ? (
            <p className="text-xs text-muted-foreground py-4 text-center">No allocations on this order.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">SKU</TableHead>
                  <TableHead className="text-right text-xs">Ordered</TableHead>
                  <TableHead className="text-right text-xs">Packed</TableHead>
                  <TableHead className="text-right text-xs">Remaining</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {skuRollup.map((r) => {
                  const remaining = r.ordered - r.packed;
                  return (
                    <TableRow key={r.sku_id}>
                      <TableCell className="text-xs">{skuLabel(r.sku_id)}</TableCell>
                      <TableCell className="text-right tabular-nums text-xs">{r.ordered}</TableCell>
                      <TableCell className="text-right tabular-nums text-xs">{r.packed}</TableCell>
                      <TableCell className={`text-right tabular-nums text-xs ${remaining === 0 ? "text-success" : "text-warning"}`}>
                        {remaining}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-3 lg:grid-cols-2">
        {cartons.map((c, idx) => (
          <Card key={c.id} className={idx === active ? "ring-2 ring-primary" : ""} onClick={() => setActive(idx)}>
            <CardHeader className="flex flex-row items-center justify-between py-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Package className="h-4 w-4" />Carton {idx + 1}
                {idx === active && <Badge variant="default" className="text-[10px]">Active</Badge>}
              </CardTitle>
              <div className="flex items-center gap-2">
                <Input
                  type="number" step="0.1" placeholder="Weight (oz)" className="w-28 h-8 text-xs"
                  value={displayCartonOz(c)}
                  onChange={(e) => setCartons((p) => p.map((x, i) => i === idx ? { ...x, weight_oz: e.target.value, weight_manual: true } : x))}
                  onClick={(e) => e.stopPropagation()}
                  title={c.weight_manual ? "Manual weight (click Auto to reset)" : "Auto-calculated from items"}
                />
                {c.weight_manual && (
                  <Button size="sm" variant="ghost" className="h-8 px-2 text-[10px]"
                    onClick={(e) => { e.stopPropagation(); setCartons((p) => p.map((x, i) => i === idx ? { ...x, weight_manual: false, weight_oz: "" } : x)); }}>
                    Auto
                  </Button>
                )}
                {cartons.length > 1 && (
                  <Button size="icon" variant="ghost" onClick={(e) => { e.stopPropagation(); removeCarton(idx); }}>
                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="flex flex-wrap items-center gap-2 pb-3" onClick={(e) => e.stopPropagation()}>
                <Label className="text-xs text-muted-foreground">Carton size</Label>
                <Select
                  value={c.size}
                  onValueChange={(v) => setCartons((p) => p.map((x, i) => (i === idx ? { ...x, size: v } : x)))}
                >
                  <SelectTrigger className="h-8 w-56 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CARTON_SIZES.map((s) => (
                      <SelectItem key={s.code} value={s.code} className="text-xs">{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="ml-auto flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => printCartonLabel(idx)}>
                    <Printer className="h-3.5 w-3.5 mr-1" />Print label
                  </Button>
                </div>
              </div>
              {c.items.length === 0 ? (
                <p className="text-xs text-muted-foreground py-4 text-center">Empty — scan items to add</p>
              ) : (
                <Table>
                  <TableHeader><TableRow><TableHead className="text-xs">SKU</TableHead><TableHead className="text-right text-xs">Qty</TableHead><TableHead className="text-xs">Serials</TableHead><TableHead></TableHead></TableRow></TableHeader>
                  <TableBody>
                    {c.items.map((i) => (
                      <TableRow key={i.allocation_id}>
                        <TableCell className="text-xs">{i.sku_label}</TableCell>
                        <TableCell className="text-right tabular-nums text-xs">{i.qty}</TableCell>
                        <TableCell className="text-xs">
                          {i.track_serials ? (
                            <div className="space-y-1" onClick={(e) => e.stopPropagation()}>
                              {Array.from({ length: i.qty }).map((_, sIdx) => (
                                <Input
                                  key={sIdx}
                                  className="h-7 text-xs font-mono"
                                  placeholder={`Serial ${sIdx + 1}`}
                                  value={i.serials[sIdx] ?? ""}
                                  onChange={(e) => setCartons((p) => p.map((x, xi) => xi !== idx ? x : {
                                    ...x,
                                    items: x.items.map((it) => it.allocation_id !== i.allocation_id ? it : {
                                      ...it,
                                      serials: Object.assign([...it.serials], { [sIdx]: e.target.value }),
                                    }),
                                  }))}
                                />
                              ))}
                              <p className={`text-[10px] ${i.serials.filter(Boolean).length === i.qty ? "text-success" : "text-warning"}`}>
                                {i.serials.filter(Boolean).length}/{i.qty} captured
                              </p>
                            </div>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button size="icon" variant="ghost" onClick={(e) => { e.stopPropagation(); removeItem(idx, i.allocation_id); }}>
                            <Trash2 className="h-3 w-3 text-muted-foreground" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={addCarton}><Plus className="h-4 w-4 mr-2" />Add carton</Button>
      </div>

      {/* Ship — carrier, service, rate, tracking, label options */}
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-sm">Ship</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1">
            <Label>Freight mode</Label>
            <Select value={freightMode} onValueChange={(v) => setFreightMode(v as typeof freightMode)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="parcel">Parcel (small package)</SelectItem>
                <SelectItem value="ltl">LTL (less-than-truckload)</SelectItem>
                <SelectItem value="partial_tl">Partial Truckload</SelectItem>
                <SelectItem value="ftl">Full Truckload (FTL)</SelectItem>
              </SelectContent>
            </Select>
            {freightMode !== "parcel" && (
              <p className="text-xs text-muted-foreground">
                A Bill of Lading will be required. Label purchase is disabled — the carrier provides the freight label.
              </p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Carrier</Label>
              {freightMode === "parcel" ? (
                <Select value={carrier} onValueChange={(v) => { setCarrier(v); setService(""); }}>
                  <SelectTrigger><SelectValue placeholder="Select carrier" /></SelectTrigger>
                  <SelectContent>
                    {CARRIERS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  value={carrier}
                  onChange={(e) => setCarrier(e.target.value)}
                  placeholder="e.g. Old Dominion, XPO, Saia"
                />
              )}
            </div>
            <div className="space-y-1">
              <Label>Service type</Label>
              {freightMode === "parcel" ? (
                <Select value={service} onValueChange={setService} disabled={!carrier || activeServices.length === 0}>
                  <SelectTrigger>
                    <SelectValue placeholder={carrier ? (activeServices.length === 0 ? "No services enabled" : "Select service") : "Pick a carrier first"}>
                      {service ? serviceDisplayFor(service) : undefined}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {activeServices.map((s) => (
                      <SelectItem key={s.service_code} value={s.service_code}>{s.display_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  value={service}
                  onChange={(e) => setService(e.target.value)}
                  placeholder="e.g. Standard LTL, Guaranteed AM"
                />
              )}
            </div>
          </div>
          <RoutingExplanationPanel orderId={order.id} />
          <RateShopPanel
            orderId={order.id}
            weightGrams={Math.round(cartons.reduce((sum, c) => sum + effectiveCartonOz(c), 0) * OZ_TO_G)}
            onChoose={(r) => {
              setChosenRate({
                carrier: r.carrier, service: r.service, amount: r.amount,
                connection_id: r.connection_id,
                service_code: r.service_code,
                estimated_delivery_date: r.estimated_delivery_date ?? null,
                estimated_delivery_source: r.estimated_delivery_source,
              });
              const cNorm = normalizeCarrier(r.carrier);
              if (cNorm) setCarrier(cNorm);
              // Store the provider-native service_code so it round-trips to
              // the label-purchase call. Display name comes from the catalog.
              if (r.service_code) setService(r.service_code);
            }}
          />
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Tracking number</Label>
              <Input value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="Optional master tracking" />
            </div>
            <div className="space-y-1">
              <Label>Selected rate</Label>
              <Input
                readOnly
                value={chosenRate ? `${chosenRate.carrier} · ${chosenRate.service} · $${chosenRate.amount.toFixed(2)}` : ""}
                placeholder="Rate-shop above to choose"
              />
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground">
            SSCC labels (when the customer's routing guide requires them) are
            allocated server-side at Pack Confirm and printable from the
            Shipment page. The pack station no longer generates SSCCs.
          </p>
        </CardContent>
      </Card>

      <div className="flex items-center justify-end">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="touch" onClick={() => navigate({ to: "/app/pack" })}>Cancel</Button>
          {freightMode === "parcel" ? (
            <>
              <Button
                variant="outline"
                size="touch"
                onClick={() => finalize("ship_only")}
                disabled={busy}
                title="Mark shipped without purchasing a label (customer-provided label)"
              >
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
                Ship
              </Button>
              <Button size="touch" onClick={() => finalize("label")} disabled={busy}>
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Printer className="h-4 w-4 mr-2" />}
                Label &amp; Ship
              </Button>
            </>
          ) : (
            <Button size="touch" onClick={() => finalize("ship_only")} disabled={busy}>
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Printer className="h-4 w-4 mr-2" />}
              Create Freight Shipment &amp; BOL
            </Button>
          )}
        </div>
      </div>
      </div>
    </>
  );
}